import { z } from 'zod'
import { productSummarySchema } from './catalog.ts'
import { minorAmountSchema } from './money.ts'

/**
 * What a particular customer pays.
 *
 * A mediator's biggest contractor does not pay the shelf price, and until now the
 * only way to give them a better one was to negotiate every order. A price book is
 * that arrangement written down once: a standing discount off the published ladder,
 * plus a flat rate on the handful of items the deal was actually about.
 *
 * Two rules keep it from becoming a pricing engine:
 *
 * - **One book per customer.** "Which of these four books wins" is a question with
 *   no good answer at a counter, so it is not a question that can be asked.
 * - **It only ever moves the price down.** A book that quietly charged somebody
 *   more than the shelf price is a bug that would be found by a customer, and the
 *   server clamps rather than trusting whoever typed the number.
 */

export const PRICE_BOOK_STATUSES = ['active', 'archived'] as const
export type PriceBookStatus = (typeof PRICE_BOOK_STATUSES)[number]

export const priceBookEntrySchema = z.object({
  productId: z.string(),
  product: productSummarySchema,
  unitPriceMinor: z.number().int(),
})
export type PriceBookEntry = z.infer<typeof priceBookEntrySchema>

export const priceBookMemberSchema = z.object({
  orgId: z.string().nullable(),
  userId: z.string().nullable(),
  name: z.string(),
})
export type PriceBookMember = z.infer<typeof priceBookMemberSchema>

export const priceBookSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** Off every published price. 500 = 5% cheaper than the shelf. */
  discountBps: z.number().int(),
  note: z.string().nullable(),
  status: z.enum(PRICE_BOOK_STATUSES),
  entries: z.array(priceBookEntrySchema),
  members: z.array(priceBookMemberSchema),
  version: z.number().int(),
  updatedAt: z.number().int(),
})
export type PriceBook = z.infer<typeof priceBookSchema>

/** 25% is already a lot for a standing arrangement; past that, negotiate the order. */
export const MAX_PRICE_BOOK_DISCOUNT_BPS = 2500

export const savePriceBookSchema = z.object({
  name: z.string().trim().min(1, 'Give the arrangement a name.').max(80),
  discountBps: z
    .number()
    .int()
    .min(0)
    .max(MAX_PRICE_BOOK_DISCOUNT_BPS, 'That is more than a standing discount should be.')
    .default(0),
  note: z.string().trim().max(300).nullish(),
  status: z.enum(PRICE_BOOK_STATUSES).optional(),
  version: z.number().int().optional(),
})
export type SavePriceBookInput = z.infer<typeof savePriceBookSchema>

export const setPriceBookEntrySchema = z.object({
  productId: z.string().min(1),
  /** Null removes the flat rate and puts the item back on the book's discount. */
  unitPriceMinor: minorAmountSchema.nullable(),
})
export type SetPriceBookEntryInput = z.infer<typeof setPriceBookEntrySchema>

export const setPriceBookMemberSchema = z
  .object({
    orgId: z.string().max(64).nullish(),
    userId: z.string().max(64).nullish(),
  })
  .refine((v) => Boolean(v.orgId) !== Boolean(v.userId), {
    message: 'Pick exactly one customer.',
    path: ['orgId'],
  })
export type SetPriceBookMemberInput = z.infer<typeof setPriceBookMemberSchema>
